import React, { useMemo } from "react";

import {
  Checkbox,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";

import { DevGateList } from "api/models";
import { resources } from "api/resources";
import { useQuery } from "api/useQuery";
import {
  AppTypography,
  Header2,
  PageLoadStateProvider,
  TableCell
} from "components/common";
import { TranslationProvider } from "components/useTranslations";
import {
  DEV_ENVIRONMENT,
  PRODUCTION_ENVIRONMENT,
  STAGE_ENVIRONMENT,
  TEST_ENVIRONMENT
} from "constants/environments";

import useDevGatesPageStyles from "./useDevGatesPageStyles";

const DevGatesPage: React.FC = () => {
  const classes = useDevGatesPageStyles();
  const { data: devGateResponse, isLoading } = useQuery<DevGateList>(
    resources.devGates
  );

  const devGates = useMemo(() => {
    return devGateResponse?.devGates ?? [];
  }, [devGateResponse?.devGates]);

  return (
    <PageLoadStateProvider isLoaded={!isLoading}>
      <TranslationProvider translations={devGateResponse?.translations}>
        {t => (
          <>
            <div className={classes.headerStyling}>
              <Header2>{t`__DEV_GATES_INITIAL_VIEW_TITLE`}</Header2>
            </div>
            <TableContainer component={Paper}>
              <Table aria-label="dev-gates-table">
                <TableHead>
                  <TableRow>
                    <TableCell>{t`__DEV_GATES_INITIAL_VIEW_NAME_COLUMN`}</TableCell>
                    <TableCell>{t`__DEV_GATES_INITIAL_VIEW_DEV_COLUMN`}</TableCell>
                    <TableCell>{t`__DEV_GATES_INITIAL_VIEW_TEST_COLUMN`}</TableCell>
                    <TableCell>{t`__DEV_GATES_INITIAL_VIEW_STAGE_COLUMN`}</TableCell>
                    <TableCell>{t`__DEV_GATES_INITIAL_VIEW_PRODUCTION_COLUMN`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devGates.length === 0 && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <AppTypography>{t`__DEV_GATES_INITIAL_VIEW_NONE_AVAILABLE`}</AppTypography>
                      </TableCell>
                    </TableRow>
                  )}
                  {devGates.map(gate => (
                    <TableRow key={gate.name}>
                      <TableCell>
                        <AppTypography>{gate.name}</AppTypography>
                      </TableCell>
                      <TableCell>
                        {renderCheckbox(
                          gate.name,
                          DEV_ENVIRONMENT,
                          gate.isDevEnabled
                        )}
                      </TableCell>
                      <TableCell>
                        {renderCheckbox(
                          gate.name,
                          TEST_ENVIRONMENT,
                          gate.isTestEnabled
                        )}
                      </TableCell>
                      <TableCell>
                        {renderCheckbox(
                          gate.name,
                          STAGE_ENVIRONMENT,
                          gate.isStageEnabled
                        )}
                      </TableCell>
                      <TableCell>
                        {renderCheckbox(
                          gate.name,
                          PRODUCTION_ENVIRONMENT,
                          gate.isProductionEnabled
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </TranslationProvider>
    </PageLoadStateProvider>
  );
};

const renderCheckbox = (
  gateName: string,
  environment: string,
  isChecked: boolean
) => (
  <FormControlLabel
    control={
      <Checkbox
        color="primary"
        checked={isChecked}
        disabled
        inputProps={{ "aria-label": `${gateName} ${environment} switch` }}
      />
    }
    label=""
  />
);

export default DevGatesPage;
